import { LoadingButton, LoadingButtonProps } from '@mui/lab'
import MuiButton, { ButtonProps as MuiButtonProps } from '@mui/material/Button'
import { ElementType, useMemo } from 'react'

type AcceptedMuiProps<C extends ElementType> =
    // Omit href to enforce the use of routes
    Omit<MuiButtonProps<C, { component?: C }>, 'href'>

type AcceptedLoadingProps<C extends ElementType> = Omit<LoadingButtonProps<C, { component?: C }>, 'href'>

type CustomProps<C extends ElementType = 'button'> = {
    /**
     * Nudge the button to the left ('start') or right ('end') to align its text with content above or below.
     * Mainly intended for `variant=text`.
     *
     * @default undefined
     */
    edge?: 'start' | 'end'

    // Adding context on top of MUI's comment for this prop
    /**
     * 	Applies to both desktop and mobile.
     */
    fullWidth?: AcceptedMuiProps<C>['fullWidth']

    /**
     * 	If `true`, the button will take up the full width of its container. Only applies to mobile.
     *
     * @default false
     */
    fullWidthOnMobile?: boolean
}

export type ButtonProps<C extends ElementType = 'button'> = AcceptedMuiProps<C> & CustomProps & AcceptedLoadingProps<C>

function Button<C extends ElementType = 'button'>(props: ButtonProps<C>): JSX.Element {
    const { fullWidthOnMobile, edge, ...otherProps } = props
    const className = useMemo(() => {
        const classes = []

        if (edge === 'start') {
            classes.push('edgeStart')
        } else if (edge === 'end') {
            classes.push('edgeEnd')
        }

        if (fullWidthOnMobile) {
            classes.push('fullWidthOnMobile')
        }

        return classes.join(' ')
    }, [edge, fullWidthOnMobile])
    const Root = useMemo(() => (typeof props.loading !== 'undefined' ? LoadingButton : MuiButton), [props.loading])

    return (
        <Root
            variant="contained" // Differs from the MUI default value of `text`
            disableElevation // Differs from the MUI default value of `false`
            className={className}
            {...otherProps}
        />
    )
}

Button.displayName = 'DSButton'

export default Button
